import React, { useState, useEffect, useRef } from "react";
import { Avatar, Badge, Button, Input, List, Typography, Tooltip, Space, Table, Tag } from "antd";
import {
  SendOutlined,
  CloseOutlined,
  MessageOutlined,
  UserOutlined,
  DeleteOutlined,
  RobotOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { Text, Paragraph } = Typography;

const STORAGE_KEY = "admin_chat_messages_v4";
const CONTEXT_KEY = "admin_chat_context_v1";
const TOP_SEARCHED_KEY = "admin_chat_top_searched_v1";

const SUGGESTION_BLOCKLIST = [
  "hi",
  "hello",
  "hey",
  "assalam",
  "assalam o alaikum",
  "salam",
  "thanks",
  "thank you",
  "bye",
  "goodbye",
  "how are you",
  "how r you",
];

const DEFAULT_SUGGESTIONS = [
  "orders today",
  "orders last 7 days",
  "revenue this month",
  "order stats this month",
  "urgent orders",
  "out of stock",
  "stock under 5",
  "complaints pending",
  "production queue ottoman",
  "production queue cloud",
  "orders by size this month",
  "users count by role",
  "storage prices",
  "top products this month",
  "top customers this month",
  "help",
];

function titleize(key) {
  return String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTime(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMins = Math.floor((now - date) / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  return date.toLocaleDateString();
}

function formatCurrency(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return v ?? "-";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR" }).format(n);
}

function formatDate(v) {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v ?? "-";
  return d.toLocaleString();
}

function statusTag(value) {
  const s = String(value || "").toLowerCase();
  if (!s) return <Tag>-</Tag>;
  if (["pending", "processing", "in progress", "in-progress"].includes(s)) return <Tag color="gold">{value}</Tag>;
  if (["approved", "completed", "resolved", "shipped", "complete", "delivered"].includes(s)) return <Tag color="green">{value}</Tag>;
  if (["rejected", "cancelled", "canceled"].includes(s)) return <Tag color="red">{value}</Tag>;
  if (["urgent", "high"].includes(s)) return <Tag color="volcano">{value}</Tag>;
  return <Tag>{value}</Tag>;
}

const DEFAULT_HIDDEN_KEYS = new Set(["id", "createdat", "updatedat", "userid", "productid", "__rowkey"]);

function buildColumnsFromServer(columnsFromServer = [], rows = []) {
  const cols =
    columnsFromServer.length > 0
      ? columnsFromServer
      : Object.keys(rows?.[0] || {})
          .filter((k) => !DEFAULT_HIDDEN_KEYS.has(String(k).toLowerCase()))
          .map((k) => ({ key: k, title: titleize(k), dataIndex: k }));

  return cols.map((c) => {
    const key = c.dataIndex || c.key;
    const lower = String(key).toLowerCase();

    return {
      title: c.title || titleize(key),
      dataIndex: key,
      key: key,
      render: (v) => {
        if (v === null || v === undefined || v === "") return "-";
        if (lower.includes("price") || lower.includes("revenue") || lower.includes("cost")) return formatCurrency(v);
        if (lower.endsWith("at") || lower.includes("date")) return formatDate(v);
        if (lower.includes("status") || lower.includes("priority")) return statusTag(v);
        return String(v);
      },
    };
  });
}

/* =======================
   ✅ EXPORT HELPERS (CSV for Excel)
   ======================= */

function sanitizeFilename(name) {
  const base = String(name || "export").trim() || "export";
  return base.replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ").trim();
}

function csvEscape(value) {
  const s = value == null ? "" : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportTableToCsv(table) {
  if (!table?.rows?.length) return;

  // keep only real data columns (exclude Actions + hidden)
  const exportCols = (table.columns || [])
    .map((c) => {
      const key = c.dataIndex || c.key;
      const title = typeof c.title === "string" ? c.title : titleize(key);
      return { key, title };
    })
    .filter((c) => c.key && c.key !== "__rowKey" && c.key !== "__actions");

  const header = exportCols.map((c) => csvEscape(c.title)).join(",");

  const lines = table.rows.map((row) => {
    return exportCols
      .map((c) => {
        const v = row?.[c.key];

        // React elements / objects should not break export
        if (v && typeof v === "object") return csvEscape(JSON.stringify(v));
        return csvEscape(v);
      })
      .join(",");
  });

  // ✅ BOM makes Excel open UTF-8 properly
  const csv = "\ufeff" + [header, ...lines].join("\n");

  const filename = `${sanitizeFilename(table.title || "results")}.csv`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

/* =======================
   Top Searches
   ======================= */

function loadTopSearches() {
  try {
    const raw = localStorage.getItem(TOP_SEARCHED_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}

function saveTopSearches(obj) {
  try {
    localStorage.setItem(TOP_SEARCHED_KEY, JSON.stringify(obj));
  } catch {
    // ignore
  }
}

function normalizeQuery(q) {
  return String(q || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function isBlockedSuggestion(q) {
  const x = normalizeQuery(q);
  return SUGGESTION_BLOCKLIST.some((b) => x === normalizeQuery(b) || x.startsWith(normalizeQuery(b) + " "));
}

function trackTopQuery(q) {
  const query = normalizeQuery(q);
  if (!query) return;
  if (isBlockedSuggestion(query)) return;

  const data = loadTopSearches();
  data[query] = (data[query] || 0) + 1;

  const keys = Object.keys(data);
  if (keys.length > 200) {
    keys.sort((a, b) => (data[a] || 0) - (data[b] || 0));
    const removeCount = keys.length - 200;
    for (let i = 0; i < removeCount; i++) delete data[keys[i]];
  }

  saveTopSearches(data);
}

function getTopSuggestions(limit = 8) {
  const data = loadTopSearches();
  const entries = Object.entries(data)
    .filter(([q]) => q && !isBlockedSuggestion(q))
    .sort((a, b) => (b[1] || 0) - (a[1] || 0))
    .map(([q]) => q);

  const base = entries.length ? entries : DEFAULT_SUGGESTIONS;

  const uniq = [];
  for (const s of base) {
    const v = normalizeQuery(s);
    if (!v || isBlockedSuggestion(v)) continue;
    if (!uniq.includes(v)) uniq.push(v);
    if (uniq.length >= limit) break;
  }
  if (!uniq.includes("help")) uniq.push("help");

  return uniq.slice(0, limit);
}

function isActionIntent(intent) {
  return ["ORDERS_LIST", "ORDER_LOOKUP", "PRODUCTION_QUEUE"].includes(String(intent || ""));
}

function bestOrderToken(row) {
  return row?.orderId || row?.orderNo || row?.trackingId || row?.referenceNumber || row?.customerRef || row?.referenceNo || row?.id || "";
}

export default function ChatWidget({ visible = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [msgs, setMsgs] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);

  const cacheRef = useRef(new Map());
  const unreadRef = useRef(0);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  const getContext = () => {
    try {
      const raw = sessionStorage.getItem(CONTEXT_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const setContext = (ctx) => {
    sessionStorage.setItem(CONTEXT_KEY, JSON.stringify(ctx || {}));
  };

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(msgs));
    if (listRef.current) {
      setTimeout(() => {
        listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
      }, 100);
    }
  }, [msgs]);

  useEffect(() => {
    if (open) {
      unreadRef.current = 0;
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const clearConversation = () => {
    setMsgs([]);
    cacheRef.current.clear();
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(CONTEXT_KEY);
  };

  const pushAssistant = (payload) => {
    setMsgs((m) => [
      ...m,
      {
        id: `a-${Date.now()}`,
        role: "assistant",
        ts: Date.now(),
        text: payload.text || "",
        table: payload.table || null,
        suggestions: payload.suggestions || [],
        meta: payload.meta || {},
        kind: payload.kind || "text",
      },
    ]);

    if (payload?.meta?.intent) setContext({ lastIntent: payload.meta.intent });
  };

  const sendMessage = async (overrideText) => {
    const question = (overrideText ?? q).trim();
    if (!question) return;

    trackTopQuery(question);
    setMsgs((m) => [...m, { id: `u-${Date.now()}`, role: "user", text: question, ts: Date.now() }]);
    setQ("");

    if (cacheRef.current.has(question)) {
      pushAssistant(cacheRef.current.get(question));
      return;
    }

    setLoading(true);
    try {
      const context = getContext();
      const { data } = await axiosInstance.post("/admin/chat", { question, context });

      const answerText = data?.answer || "OK";
      const result = data?.result || {};
      const rows = Array.isArray(result.rows) ? result.rows : [];
      const serverCols = Array.isArray(result.columns) ? result.columns : [];
      const columns = buildColumnsFromServer(serverCols, rows);
      const intent = result?.meta?.intent;

      const finalColumns = [...columns];
      if (isActionIntent(intent)) {
        finalColumns.push({
          title: "Actions",
          key: "__actions",
          render: (_, row) => (
            <Space size={6} wrap>
              <Button size="small" onClick={() => sendMessage(`order status ${bestOrderToken(row)}`.trim())}>
                View Order
              </Button>
              {row.userId != null ? (
                <Button size="small" onClick={() => sendMessage(`user details ${row.userId}`)}>
                  View User
                </Button>
              ) : null}
              {row.productId != null ? (
                <Button size="small" onClick={() => sendMessage(`product ${row.productId}`)}>
                  View Product
                </Button>
              ) : null}
            </Space>
          ),
        });
      }

      const payload = {
        text: answerText,
        kind: result.kind || "text",
        suggestions: result.suggestions || [],
        meta: result.meta || {},
        table:
          rows.length > 0
            ? {
                title: result.title || "Results",
                rows: rows.map((r, i) => ({ __rowKey: i, ...r })),
                columns: finalColumns,
                rowCount: result.rowCount ?? rows.length,
              }
            : null,
      };

      cacheRef.current.set(question, payload);
      pushAssistant(payload);

      if (!open) unreadRef.current += 1;
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        status === 429
          ? "Too many requests. Please wait a moment and try again."
          : status === 401
          ? "Session expired. Please log in again."
          : "I'm having trouble connecting. Please try again.";

      pushAssistant({ text: msg, kind: "text", suggestions: ["help"] });
      if (!open) unreadRef.current += 1;
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!visible) return null;

  const emptySuggestions = getTopSuggestions(8);
  const TOOLTIP_Z = 10050;

  return (
    <>
      {open && <div style={styles.backdrop} onMouseDown={() => setOpen(false)} />}

      {!open && (
        <div style={styles.floatingButtonWrap}>
          <Tooltip title="Ask Assistant" placement="left" color="#2563eb" zIndex={TOOLTIP_Z}>
            <Badge dot={unreadRef.current > 0} offset={[-5, 5]} size="small" color="#3b82f6">
              <Button
                shape="circle"
                size="large"
                onClick={() => setOpen(true)}
                style={styles.floatingButton}
                className="chat-bubble-button"
                icon={<MessageOutlined style={{ fontSize: 24, color: "#fff" }} />}
              />
            </Badge>
          </Tooltip>
        </div>
      )}

      {open && (
        <div ref={panelRef} style={styles.panelContainer} className="chat-panel" onMouseDown={(e) => e.stopPropagation()}>
          <div style={styles.panelHeader}>
            <Space size={16}>
              <div style={styles.headerAvatarWrapper}>
                <Avatar size={48} icon={<RobotOutlined />} style={styles.headerAvatar} />
                <div style={styles.statusIndicator} />
              </div>
              <div>
                <div style={styles.headerTitle}>AI Assistant</div>
                <Space size={4}>
                  <CheckCircleOutlined style={styles.statusIcon} />
                  <Text style={styles.headerSubtitle}>Online · Ready to help</Text>
                </Space>
              </div>
            </Space>

            <Space size={8}>
              <Tooltip title="Clear conversation" color="#2563eb" zIndex={TOOLTIP_Z}>
                <Button size="middle" icon={<DeleteOutlined />} onClick={clearConversation} style={styles.iconButton} />
              </Tooltip>

              <Tooltip title="Close" color="#2563eb" zIndex={TOOLTIP_Z}>
                <Button size="middle" icon={<CloseOutlined />} onClick={() => setOpen(false)} style={styles.iconButton} />
              </Tooltip>
            </Space>
          </div>

          <div style={styles.panelBody} ref={listRef}>
            {msgs.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIconWrapper}>
                  <MessageOutlined style={styles.emptyIcon} />
                </div>
                <Text strong style={styles.emptyTitle}>Welcome</Text>
                <Text style={styles.emptyText}>Ask about orders, revenue, urgent orders, stock, complaints, production…</Text>

                <div style={styles.suggestionChips}>
                  {emptySuggestions.map((s) => (
                    <Button key={s} block size="middle" style={styles.suggestionTag} onClick={() => sendMessage(s)} disabled={loading}>
                      {s}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <List
                dataSource={msgs}
                split={false}
                renderItem={(m, index) => {
                  const isUser = m.role === "user";
                  const showAvatar = index === 0 || msgs[index - 1]?.role !== m.role;

                  return (
                    <List.Item style={styles.messageItem}>
                      <div style={{ ...styles.messageContainer, flexDirection: isUser ? "row-reverse" : "row" }}>
                        {showAvatar ? (
                          <div style={styles.avatarContainer}>
                            <Avatar
                              size={40}
                              icon={isUser ? <UserOutlined /> : <RobotOutlined />}
                              style={{ ...styles.messageAvatar, background: isUser ? styles.userGradient : styles.assistantGradient }}
                            />
                          </div>
                        ) : (
                          <div style={{ width: 40, margin: "0 8px" }} />
                        )}

                        <div
                          style={{
                            ...styles.messageBubble,
                            ...(isUser ? styles.userBubble : styles.assistantBubble),
                            maxWidth: showAvatar ? "calc(100% - 64px)" : "100%",
                          }}
                        >
                          {showAvatar && (
                            <div style={styles.messageHeader}>
                              <Text strong style={{ fontSize: 13, color: isUser ? "#fff" : "#1e293b" }}>
                                {isUser ? "You" : "AI Assistant"}
                              </Text>

                              <span style={styles.messageHeaderRight}>
                                <ClockCircleOutlined
                                  style={{ fontSize: 10, color: isUser ? "rgba(255,255,255,0.7)" : "#94a3b8" }}
                                />
                                <Text style={{ fontSize: 11, color: isUser ? "rgba(255,255,255,0.7)" : "#64748b" }}>
                                  {formatTime(m.ts)}
                                </Text>
                              </span>
                            </div>
                          )}

                          <Paragraph style={{ margin: 0, whiteSpace: "pre-wrap", color: isUser ? "#fff" : "#1e293b" }}>
                            {m.text}
                          </Paragraph>

                          {!isUser && m.table?.rows?.length > 0 && (
                            <div style={{ marginTop: 10 }}>
                              {/* ✅ table top bar + export */}
                              <div style={styles.tableTopBar}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                  <Tag color="blue">{m.table.title}</Tag>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    Rows: {m.table.rowCount ?? m.table.rows.length}
                                  </Text>
                                </div>

                                <Tooltip title="Export to Excel (CSV)" color="#2563eb" zIndex={TOOLTIP_Z}>
                                  <Button
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    onClick={() => exportTableToCsv(m.table)}
                                  >
                                    Export
                                  </Button>
                                </Tooltip>
                              </div>

                              <Table
                                size="small"
                                bordered
                                pagination={false}
                                columns={m.table.columns}
                                dataSource={m.table.rows}
                                rowKey="__rowKey"
                                scroll={{ x: "max-content" }}
                                style={{ background: "#fff", borderRadius: 12, overflow: "hidden" }}
                              />
                            </div>
                          )}

                          {!isUser && Array.isArray(m.suggestions) && m.suggestions.length > 0 && (
                            <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
                              {m.suggestions.slice(0, 6).map((s) => (
                                <Button key={s} size="small" onClick={() => sendMessage(s)} disabled={loading}>
                                  {s}
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </List.Item>
                  );
                }}
              />
            )}
          </div>

          <div style={styles.panelFooter}>
            <div style={styles.inputWrapper}>
              <Input.TextArea
                ref={inputRef}
                autoSize={{ minRows: 1, maxRows: 4 }}
                placeholder="Type your message..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={handleKeyDown}
                style={styles.textArea}
                disabled={loading}
              />
              <Button
                type="primary"
                shape="circle"
                icon={<SendOutlined />}
                loading={loading}
                onClick={() => sendMessage()}
                disabled={!q.trim()}
                style={styles.sendButton}
              />
            </div>
            <div style={styles.footerInfo}>
              <Text style={styles.footerText}>
                <span style={styles.keyHint}>↵</span> Enter
              </Text>
              <Text style={styles.footerText}>
                <span style={styles.keyHint}>⇧</span> Shift+Enter
              </Text>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  backdrop: { position: "fixed", inset: 0, background: "transparent", zIndex: 9998 },

  floatingButtonWrap: { position: "fixed", right: 28, bottom: 28, zIndex: 9999 },
  floatingButton: { background: "linear-gradient(145deg,#3b82f6,#2563eb)", border: "none", width: 68, height: 68 },

  panelContainer: {
    position: "fixed",
    right: 28,
    bottom: 28,
    width: 440,
    maxWidth: "calc(100vw - 56px)",
    height: 680,
    maxHeight: "calc(100vh - 56px)",
    zIndex: 9999,
    background: "#fff",
    borderRadius: 28,
    boxShadow: "0 40px 80px rgba(0,0,0,0.2)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px",
    borderBottom: "1px solid #e2e8f0",
    background: "#fff",
  },

  headerAvatarWrapper: { position: "relative" },
  headerAvatar: { background: "linear-gradient(145deg,#3b82f6,#2563eb)", color: "#fff", border: "3px solid #fff" },
  statusIndicator: { position: "absolute", bottom: 2, right: 2, width: 12, height: 12, background: "#10b981", borderRadius: "50%", border: "2px solid #fff" },
  headerTitle: { fontWeight: 700, fontSize: 18, color: "#0f172a" },
  headerSubtitle: { fontSize: 12, color: "#64748b" },
  statusIcon: { fontSize: 12, color: "#10b981" },
  iconButton: { border: "none", color: "#64748b", background: "transparent" },

  panelBody: { flex: 1, overflowY: "auto", padding: 24, background: "#f8fafc" },

  emptyState: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100%", textAlign: "center", padding: "12px 6px" },
  emptyIconWrapper: { background: "#fff", borderRadius: "50%", padding: 20, marginBottom: 16, boxShadow: "0 20px 40px rgba(0,0,0,0.08)" },
  emptyIcon: { fontSize: 56, color: "#3b82f6" },
  emptyTitle: { fontSize: 22, color: "#0f172a", marginBottom: 8, fontWeight: 700 },
  emptyText: { color: "#64748b", fontSize: 14, maxWidth: 300 },

  suggestionChips: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,
    width: "100%",
    maxWidth: 420,
    marginTop: 14,
  },
  suggestionTag: {
    borderRadius: 30,
    height: 38,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  messageItem: { padding: "6px 0", border: "none" },
  messageContainer: { display: "flex", gap: 8, width: "100%" },
  avatarContainer: { flexShrink: 0 },
  messageAvatar: { marginTop: 4, border: "2px solid #fff" },
  userGradient: "linear-gradient(145deg,#3b82f6,#2563eb)",
  assistantGradient: "linear-gradient(145deg,#94a3b8,#64748b)",
  messageBubble: { padding: "12px 16px", borderRadius: 20, wordBreak: "break-word" },
  userBubble: { background: "linear-gradient(145deg,#3b82f6,#2563eb)", color: "#fff", borderBottomRightRadius: 4 },
  assistantBubble: { background: "#fff", color: "#0f172a", borderBottomLeftRadius: 4 },

  // ✅ header time never overlaps
  messageHeader: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "space-between", marginBottom: 6 },
  messageHeaderRight: { display: "inline-flex", alignItems: "center", gap: 6, marginLeft: "auto", flexShrink: 0 },

  // ✅ export bar
  tableTopBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 8,
  },

  panelFooter: { padding: 20, borderTop: "1px solid #e2e8f0", background: "#fff" },
  inputWrapper: { display: "flex", gap: 12, alignItems: "flex-end" },
  textArea: { flex: 1, borderRadius: 24, padding: "12px 18px" },
  sendButton: { width: 44, height: 44, background: "linear-gradient(145deg,#3b82f6,#2563eb)", border: "none" },
  footerInfo: { display: "flex", justifyContent: "space-between", marginTop: 8 },
  footerText: { fontSize: 11, color: "#64748b" },
  keyHint: { background: "#f1f5f9", padding: "2px 6px", borderRadius: 6, border: "1px solid #e2e8f0" },
};